import {DialogTitle, Tooltip} from "@mui/material";
import HelpOutline from "@mui/icons-material/HelpOutline";
import {Dialog2} from "../../../../misc/Dialog2";
import {SmoothActionBar} from "../../../../misc/SmoothActionBar";
import {PopperZIndexProvider} from "../../../../misc/scroller/PopperZIndexProvider";
import useWindowSize from "react-use/lib/useWindowSize";
import {Column, StringCol, Table} from "../../../../misc/scroller/Table";
import {Keying2Label, LibraryEntry} from "../../../../api/Keying2";
import {api} from "../../../../api/API";
import {removeRowOption, RowOptionCol} from "../../../logistics/purchasing/RowOptions";
import {useContext, useEffect, useMemo, useState} from "react";
import {EventEmitter} from "nate-react-api-helpers";
import {AutoInsert} from "../openings/AutoInsertOpening";
import {
    Checkbox,
    CheckboxExtContext,
    CheckboxExtProvider,
    CheckboxExtSelectorCol,
    useCheckboxExtSetupData
} from "../../../project/manage/sov/CheckboxExtProvider";

const tableName = "keying.label_library"
export function KeyingLibrary(props: {
    value: Keying2Label[];
    onDone(labels: Keying2Label[], shortcodeNames: string[]): void;
    onCancel(): void;
}) {
    return (
        <CheckboxExtProvider name={tableName}>
            <KeyingLibraryInner {...props} />
        </CheckboxExtProvider>
    );
}

function KeyingLibraryInner(props: {
    value: Keying2Label[];
    onDone(labels: Keying2Label[], shortcodeNames: string[]): void;
    onCancel(): void;
}) {
    const win = useWindowSize();
    const ctx = useContext(CheckboxExtContext);
    const setupData = useCheckboxExtSetupData();
    const addShortcode = useMemo(() => new EventEmitter<number[]>(), []);

    // Table memoizes columns on mount, so the cell subscribes to the emitter instead of reading state here
    const addShortcodeCol = useMemo((): Column<LibraryEntry> => ({
        name: "Add SC",
        width: 70,
        renderName: () => <Tooltip title="Auto-create shortcode too">
            <span style={{display: "inline-flex", alignItems: "center", gap: 4}}>
                Add SC <HelpOutline style={{fontSize: "1em"}} />
            </span>
        </Tooltip>,
        render: o => <div style={{marginTop: -4, marginBottom: -4}}>
            <AddShortcodeCell id={o.id} emitter={addShortcode} />
        </div>,
    }), [addShortcode]);

    return (
        <Dialog2 open onClose={() => props.onCancel()}>
            <DialogTitle>Keying Label Library</DialogTitle>
            <div>
                <PopperZIndexProvider value={1400}>
                    <div style={{
                        height: win.height * 0.60,
                        width: Math.min(win.width * 0.60, 600),
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "stretch",
                    }}>
                        <Table<LibraryEntry>
                            name={tableName}
                            columns={[
                                CheckboxExtSelectorCol<LibraryEntry>(),
                                addShortcodeCol,
                                RowOptionCol({
                                    options: (dt) => [
                                        removeRowOption(dt, {
                                            refreshTables: [tableName],
                                        })
                                    ],
                                }),
                                StringCol("Name", "name"),
                                StringCol("Title", "title"),
                                StringCol("Description", "description"),
                            ]}
                            fetch={() => setupData(api.keying2.listLibraryEntries())}
                            fetchDeps={[]}
                            onChange={input => api.keying2.upsertLibraryEntry(input)}
                            insert={{
                                buttonText: "New Label",
                                alignX: "left",
                                modal: onDone => <AutoInsert onDone={onDone} getInput={() => ({
                                    name: "",
                                    title: "",
                                    description: "",
                                })} />,
                            }}
                        />
                    </div>
                </PopperZIndexProvider>
            </div>
            <SmoothActionBar
                onCancel={() => props.onCancel()}
                actionName="Apply"
                onSubmit={() => {
                    const selected: LibraryEntry[] = ctx.data.lastValue || [];
                    const existingNames = new Set((props.value || []).map(l => l.name));

                    const newLabels: Keying2Label[] = selected
                        .filter(entry => !existingNames.has(entry.name))
                        .map(entry => ({
                            name: entry.name,
                            title: entry.title,
                            description: entry.description,
                            keyCount: 0,
                        }));

                    const addShortcodeIds = addShortcode.lastValue || [];
                    const shortcodeNames = selected
                        .filter(entry => addShortcodeIds.includes(entry.id))
                        .map(entry => shortcodeNameForLabel(entry));

                    props.onDone(newLabels, shortcodeNames);

                }}
            />
        </Dialog2>
    )
}

function AddShortcodeCell(props: {
    id: number;
    emitter: EventEmitter<number[]>;
}) {
    const [checked, setChecked] = useState(false);

    useEffect(() => {
        const sub = props.emitter.subscribeAndFireLast(ids => setChecked((ids || []).includes(props.id)));
        return () => sub.cancel();
    }, [props.emitter, props.id]);

    return (
        <Checkbox size="small" checked={checked} onClick={e => {
            e.stopPropagation();
            const ids = props.emitter.lastValue || [];
            props.emitter.emit(ids.includes(props.id) ? ids.filter(id => id !== props.id) : [...ids, props.id]);
        }} />
    )
}

function shortcodeNameForLabel(entry: {name: string; title: string}) {
    return [entry.name, entry.title].filter(v => !!v).join(" ");
}
